import { Modal, Badge, Button, confirm, toast } from "@devhop/ui";
import type { AuditIssueDTO } from "@/modules/internal-audit/domain/contracts";
import { formatDateTime } from "@/shared/lib/date-time";
import { CheckCircleIcon } from "lucide-react";

type AuditIssueDetailModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issue: AuditIssueDTO | null;
  onClose?: () => void;
};

const severityLabels: Record<string, string> = {
  low: "ເບົາ",
  medium: "ກາງ",
  high: "ສູງ",
  critical: "ວິກິດ",
};

const severityColors: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  low: "secondary",
  medium: "default",
  high: "outline",
  critical: "destructive",
};

const statusLabels: Record<string, string> = {
  open: "ເປີດ",
  in_progress: "ກຳລັງແກ້ໄຂ",
  resolved: "ແກ້ໄຂແລ້ວ",
  closed: "ປິດ",
};

const statusColors: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  open: "destructive",
  in_progress: "outline",
  resolved: "default",
  closed: "secondary",
};

export function AuditIssueDetailModal({
  open,
  onOpenChange,
  issue,
  onClose,
}: AuditIssueDetailModalProps) {
  if (!issue) return null;

  const handleClose = async () => {
    if (!onClose || !issue) return;
    
    if (issue.status === "closed") {
      toast.error("ບັນຫານີ້ຖືກປິດແລ້ວ");
      return;
    }

    const ok = await confirm({
      title: "ປິດບັນຫາ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການປິດບັນຫານີ້? ຫຼັງຈາກປິດແລ້ວຈະບໍ່ສາມາດແກ້ໄຂໄດ້.",
      actionText: "ປິດ",
      cancelText: "ຍົກເລີກ",
    });
    if (ok) {
      onClose();
      onOpenChange(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="ລາຍລະອຽດບັນຫາການກວດສອບ"
      size="lg"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-muted-foreground text-sm">ຊື່ບັນຫາ</p>
            <p className="font-medium">{issue.title}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-sm">ພົບຈາກ</p>
            <p className="font-medium">{issue.auditRecordName || "-"}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-sm">ພະແນກ</p>
            <p className="font-medium">{issue.departmentName || "-"}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-sm">ລະດັບຄວາມຮຸນແຮງ</p>
            <Badge variant={severityColors[issue.severity] || "default"}>
              {severityLabels[issue.severity] || issue.severity}
            </Badge>
          </div>
          <div>
            <p className="text-muted-foreground text-sm">ສະຖານະ</p>
            <Badge variant={statusColors[issue.status] || "default"}>
              {statusLabels[issue.status] || issue.status}
            </Badge>
          </div>
          <div>
            <p className="text-muted-foreground text-sm">ຜູ້ຮັບຜິດຊອບ</p>
            <p className="font-medium">
              {issue.assignedToUserName || issue.assignedToPositionTitle || "-"}
            </p>
          </div>
          {issue.dueDate && (
            <div>
              <p className="text-muted-foreground text-sm">ວັນທີໝາຍ</p>
              <p className="font-medium">
                {formatDateTime(issue.dueDate)}
              </p>
            </div>
          )}
          {issue.resolvedAt && (
            <div>
              <p className="text-muted-foreground text-sm">ວັນທີແກ້ໄຂ</p>
              <p className="font-medium">
                {formatDateTime(issue.resolvedAt)}
              </p>
            </div>
          )}
        </div>

        <div>
          <p className="text-muted-foreground text-sm">ລາຍລະອຽດບັນຫາ</p>
          <p className="mt-1 whitespace-pre-wrap">{issue.description}</p>
        </div>

        {issue.rootCause && (
          <div>
            <p className="text-muted-foreground text-sm">ສາເຫດ</p>
            <p className="mt-1 whitespace-pre-wrap">{issue.rootCause}</p>
          </div>
        )}

        {issue.resolution && (
          <div>
            <p className="text-muted-foreground text-sm">ວິທີແກ້ໄຂ</p>
            <p className="mt-1 whitespace-pre-wrap">{issue.resolution}</p>
          </div>
        )}

        {issue.closedAt && (
          <div>
            <p className="text-muted-foreground text-sm">ວັນທີປິດ</p>
            <p className="font-medium">
              {formatDateTime(issue.closedAt)}
              {issue.closedByName && ` (${issue.closedByName})`}
            </p>
          </div>
        )}

        <div className="border-t pt-4">
          <p className="text-muted-foreground text-xs">
            ສ້າງເມື່ອ: {formatDateTime(issue.createdAt)}
          </p>
          <p className="text-muted-foreground text-xs">
            ອັບເດດລ່າສຸດ: {formatDateTime(issue.updatedAt)}
          </p>
        </div>

        {onClose && issue.status !== "closed" && (
          <div className="border-t pt-4">
            <Button
              onClick={handleClose}
              variant="outline"
              className="w-full gap-2"
            >
              <CheckCircleIcon className="h-4 w-4" />
              ປິດບັນຫາ
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

